import React, { useState } from 'react';
import {
  Box,
  Modal,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import axios from 'axios';
import {
  API_post_file_upload_with_eagerly_generated_presigned_url,
  API_get_file_upload,
} from '../../../constants';
import { getRightworkAPI, getDefaultAxiosConfig } from '../requests/utilities/requests';

const DocumentUploadReviewModal = ({
  open,
  onClose,
  config,
  homeCompany,
  onUploadComplete,
  language,
  requiresExpirationDate,
  certificateTypeLabel
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  // YYYY-MM-DD; collected when the block maps to an expiring document type
  const [expirationDate, setExpirationDate] = useState("");

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadError(null);
      setUploadSuccess(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('Please select a file first');
      return;
    }
    if (requiresExpirationDate && !expirationDate) {
      setUploadError(getTranslation('expirationRequired'));
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      // Step 1: Get presigned URL
      const formData = new FormData();
      formData.append('company_id', homeCompany);
      formData.append('file_name', selectedFile.name);
      formData.append('type', 'lms_generic_upload_from_employee');

      const axiosConfig = await getDefaultAxiosConfig();
      const presignedUrlResponse = await axios.post(
        API_post_file_upload_with_eagerly_generated_presigned_url,
        formData,
        {
          headers: {
            ...axiosConfig.headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const fileUUID = presignedUrlResponse?.data?.redux?.payload?.uuid;
      const presignedUrl = presignedUrlResponse?.data?.redux?.payload?.presigned_url;

      if (!fileUUID || !presignedUrl) {
        throw new Error('Failed to get upload URL');
      }

      setUploadProgress(30);

      // Step 2: Upload file to S3
      await axios.put(presignedUrl, selectedFile, {
        headers: {
          'Content-Type': selectedFile.type || 'application/octet-stream',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            30 + (progressEvent.loaded * 50) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      setUploadProgress(80);

      // Step 3: Verify upload by fetching file info
      const apiParams = {
        company_id: homeCompany,
        file_upload_uuid: fileUUID,
      };
      const fileInfoResponse = await getRightworkAPI(API_get_file_upload, apiParams);
      const fileUrl = fileInfoResponse?.data?.redux?.payload?.file_upload?.file;

      setUploadProgress(100);
      setUploadSuccess(true);

      // Notify parent component of successful upload and wait for completion record to be saved
      if (onUploadComplete) {
        try {
          await onUploadComplete(
            {
              uuid: fileUUID,
              url: fileUrl,
              name: selectedFile.name,
            },
            requiresExpirationDate ? expirationDate : null
          );
        } catch (completionError) {
          console.error('Error saving completion record:', completionError);
          setUploadSuccess(false);
          throw new Error('File uploaded but failed to save completion record. Please try uploading again.');
        }
      }

      // Close modal after a brief delay (only if everything succeeded)
      setTimeout(() => {
        handleClose();
      }, 1500);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(
        error.response?.data?.message ||
        'Failed to upload file. Please try again.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      setUploadProgress(0);
      setUploadError(null);
      setUploadSuccess(false);
      setExpirationDate("");
      onClose();
    }
  };

  const getTranslation = (key) => {
    const translations = {
      title: {
        en: 'Upload Document',
        es: 'Subir Documento'
      },
      selectFile: {
        en: 'Select File',
        es: 'Seleccionar Archivo'
      },
      upload: {
        en: 'Upload',
        es: 'Subir'
      },
      uploading: {
        en: 'Uploading...',
        es: 'Subiendo...'
      },
      success: {
        en: 'Upload Successful!',
        es: '¡Subida Exitosa!'
      },
      cancel: {
        en: 'Cancel',
        es: 'Cancelar'
      },
      fileSelected: {
        en: 'File selected:',
        es: 'Archivo seleccionado:'
      },
      required: {
        en: 'Required',
        es: 'Requerido'
      },
      expirationDate: {
        en: 'Expiration date',
        es: 'Fecha de vencimiento'
      },
      expirationHelp: {
        en: 'Enter the expiration date shown on the document.',
        es: 'Ingresa la fecha de vencimiento que aparece en el documento.'
      },
      expirationRequired: {
        en: 'Please enter the expiration date',
        es: 'Por favor ingresa la fecha de vencimiento'
      }
    };

    return translations[key]?.[language] || translations[key]?.en || key;
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 600,
    bgcolor: 'background.paper',
    borderRadius: '16px',
    boxShadow: 24,
    p: 0,
    maxHeight: '90vh',
    overflowY: 'auto',
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <CloudUploadIcon className="text-violet-600" style={{ fontSize: 24 }} />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {config.title || getTranslation('title')}
              </h3>
              {config.required && (
                <span className="inline-block mt-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                  {getTranslation('required')}
                </span>
              )}
            </div>
          </div>
          <button
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
            onClick={handleClose}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {config.description && (
            <p className="mb-6 text-sm text-gray-600">
              {config.description}
            </p>
          )}

          {/* File Input */}
          <div className="mb-6">
            <input
              type="file"
              id="document-upload-input"
              className="hidden"
              onChange={handleFileSelect}
              disabled={isUploading || uploadSuccess}
            />
            <label htmlFor="document-upload-input">
              <button
                type="button"
                disabled={isUploading || uploadSuccess}
                className={`w-full py-3 px-4 border-2 border-dashed rounded-lg flex items-center justify-center gap-2 transition-colors ${
                  isUploading || uploadSuccess
                    ? 'border-gray-300 bg-gray-50 text-gray-400 cursor-not-allowed'
                    : selectedFile
                    ? 'border-violet-500 bg-violet-50 text-gray-700 hover:bg-violet-100'
                    : 'border-gray-300 text-gray-600 hover:border-violet-500 hover:bg-gray-50'
                }`}
                onClick={(e) => {
                  if (!isUploading && !uploadSuccess) {
                    document.getElementById('document-upload-input').click();
                  }
                  e.preventDefault();
                }}
              >
                <DescriptionIcon style={{ fontSize: 20 }} />
                <span className="font-medium">{getTranslation('selectFile')}</span>
              </button>
            </label>
          </div>

          {/* Selected File Info */}
          {selectedFile && (
            <div className="mb-6 p-3 bg-gray-100 rounded-lg flex items-center gap-2">
              <DescriptionIcon className="text-violet-600" />
              <div className="flex-1">
                <p className="text-xs text-gray-600">
                  {getTranslation('fileSelected')}
                </p>
                <p className="text-sm font-medium text-gray-900">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-gray-500">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
          )}

          {/* Expiration Date (certificate-mapped blocks that track expiration) */}
          {requiresExpirationDate && (
            <div className="mb-6">
              <label
                htmlFor="document-upload-expiration"
                className="block text-sm font-medium text-gray-700 mb-1.5"
              >
                {certificateTypeLabel
                  ? `${certificateTypeLabel} ${getTranslation('expirationDate').toLowerCase()}`
                  : getTranslation('expirationDate')}
              </label>
              <input
                id="document-upload-expiration"
                type="date"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
                disabled={isUploading || uploadSuccess}
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-violet-600 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
              />
              <p className="mt-1.5 text-xs text-gray-500">
                {getTranslation('expirationHelp')}
              </p>
            </div>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <div className="mb-6">
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: '#e5e7eb',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#7c3aed'
                  }
                }}
              />
              <p className="mt-2 text-xs text-gray-600">
                {getTranslation('uploading')} {uploadProgress}%
              </p>
            </div>
          )}

          {/* Success Message */}
          {uploadSuccess && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <CheckCircleIcon className="text-green-600" />
              <span className="text-sm font-medium text-green-800">
                {getTranslation('success')}
              </span>
            </div>
          )}

          {/* Error Message */}
          {uploadError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{uploadError}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex gap-3 justify-end">
          <button
            onClick={handleClose}
            disabled={isUploading}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              isUploading
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {getTranslation('cancel')}
          </button>
          <button
            onClick={handleUpload}
            disabled={
              !selectedFile ||
              (requiresExpirationDate && !expirationDate) ||
              isUploading ||
              uploadSuccess
            }
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              !selectedFile ||
              (requiresExpirationDate && !expirationDate) ||
              isUploading ||
              uploadSuccess
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-violet-600 hover:bg-violet-700 text-white'
            }`}
          >
            {isUploading ? getTranslation('uploading') : getTranslation('upload')}
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default DocumentUploadReviewModal;
